import { createSelector } from 'reselect';
import { any, append, isEmpty, isNil, find, map, path, pathOr, pipe, pluck, uniq } from 'ramda';
import { BoolHelper, i18n, MetadataConstants, MetadataSelectors, PermissionService, SessionSelectors } from 'invision-core';
import { EMPTY_ARRAY, RouteParams, SelectedCustomerSelector } from './customer.selectors';
import { CurrentActiveAddressesSelector } from './customer.addresses.selectors';
import { ISSUE_CREDIT_TO_ALTERNATE_PAYMENT_INSTRUMENT, ORDERING_ACCESS, SUBSCRIBER_PRODUCT_REMOVE_DISCOUNT_ACCESS, SUBSCRIPTION_ACCESS } from '../../security.attributes';
import LocaleKeys from './../../locales/keys';
import { GENERAL_STATUSES } from '../../components/shared/constants/general.status.constants';
import { ORDER_STATUSES } from '../../components/shared/constants/order.status.constants';
import * as SubscriptionConstants from '../../components/shared/constants/subscriptions.constants';
import { constructPricingPlanDictionary, doesCurrentSubscriptionHaveItems, setSubscriptionData } from '../helpers/customer.subscriptions.helper';
import { hasBulkOfferOptions, hasPendingChange } from '../../components/customer/dashboard/dbssDashboard/offering/offering.component.helper';
import { DASHBOARD_ROUTE, SUBSCRIPTION_LIST_ROUTE } from '../constants/dashboard.constants';
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  return state.customercare.recoverableUiState.subscriptions;
};
export var CurrentSubscriptionIdSelector = createSelector([RouteParams], function (params) {
  return params.subscriptionId;
});
export var CurrentSubscriptionItemExtensionIdSelector = createSelector([RouteParams], function (params) {
  return params.extensionId;
});
export var CurrentOfferIdSelector = createSelector([RouteParams], function (params) {
  return params.offerId;
});

// holds the id of individual subscription item from an aligned subscription being changed in modify, remove, & replace flows
export var ChildSubscriptionIdSelector = createSelector([RouteParams], function (params) {
  var result = params.childSubscriptionId;
  if (result === '') {
    return null;
  }
  return result;
});
var CurrentSubscription = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.currentSubscription;
});
export var PendingOrderSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.pendingOrder;
});
export var IsRetrievingPendingOrderSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.isRetrievingPendingOrder;
});
export var CurrentSubscriptionOfferingDetailsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.currentSubscriptionOffering.details;
});
export var IsCurrentSubscriptionOfferingPendingSelector = createSelector([CurrentSubscriptionOfferingDetailsSelector], function (selectedSubscriptionOffering) {
  return hasPendingChange(selectedSubscriptionOffering);
});
export var CurrentRemovalSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.currentRemoval;
});
export var CurrentSubscriptionIdFromStoreSelector = createSelector([CurrentSubscription], function (currentSubscription) {
  return pathOr(null, ['Id'], currentSubscription);
});
export var CurrentSubscriptionSelector = createSelector([CurrentSubscription, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.SubscriberProductPricingPlanChangeSource), MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.SubscriptionBillingRenewalType), MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.SubscriptionStateIndicator)], function (currentSubscription, pricingPlanChangeSources, renewalTypes, subscriptionStateIndicators) {
  return setSubscriptionData(currentSubscription, pricingPlanChangeSources, renewalTypes, subscriptionStateIndicators);
});
export var RemoveChildSubscriptionSelector = createSelector([ChildSubscriptionIdSelector, CurrentSubscription, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.SubscriberProductPricingPlanChangeSource), MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.SubscriptionBillingRenewalType), MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.SubscriptionStateIndicator)], function (childId, currentSubscription, pricingPlanChangeSources, renewalTypes, subscriptionStateIndicators) {
  if (isNil(childId)) {
    return null;
  }
  var targetSubscription = isNil(currentSubscription) ? null : find(function (item) {
    return item.Id === childId && item.Id !== currentSubscription.Id;
  }, currentSubscription.Items);
  var result = isNil(targetSubscription) ? null : setSubscriptionData(targetSubscription, pricingPlanChangeSources, renewalTypes, subscriptionStateIndicators);
  return result;
});
export var ShowShippingInfoSelector = createSelector([CurrentSubscriptionSelector], function (currentSubscription) {
  var showShippingInfo = false;
  if (currentSubscription && currentSubscription.Items) {
    showShippingInfo = currentSubscription.Items.some(function (item) {
      return item.Shipped || item.Children && item.Children.some(function (childItem) {
        return childItem.Shipped;
      });
    });
  }
  return showShippingInfo;
});
export var IsFetchingDataSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.isFetchingData;
});
export var IsFetchingSubscriptionDetailsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.isFetchingDetails;
});
export var IsRemovingDiscountSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.isRemovingDiscount;
});
export var IsRemovingSubscriptionSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.isRemovingSubscription;
});
var IsSubscriptionActiveSelector = createSelector([CurrentSubscriptionSelector], function (currentSubscription) {
  return currentSubscription && currentSubscription.Status === GENERAL_STATUSES.ACTIVE;
});
var IsSubscriptionSuspendedSelector = createSelector([CurrentSubscriptionSelector], function (currentSubscription) {
  return currentSubscription && currentSubscription.Status === GENERAL_STATUSES.SUSPENDED;
});
export var IsUpdatingSubscriptionSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.isUpdating;
});
export var IsValidatingCouponSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.isValidatingCoupon;
});
export var PageNumberSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.pageNumber ? uiState.pageNumber : 0;
});
export var SelectedPageSizePreference = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.pageSizePreference;
});
export var IncludeRemovedSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.includeRemoved;
});
export var RecordCountSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.recordCount ? selectedCustomer.subscriptions.recordCount : 0;
});
export var EndRecordSelector = createSelector([PageNumberSelector, SessionSelectors.PageSizePreferenceSelector, RecordCountSelector], function (pageNumber, pageSize, recordCount) {
  var endNumber = pageNumber * pageSize;
  if (endNumber < recordCount) {
    return endNumber;
  }
  return recordCount;
});
export var SelectedDiscountSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.selectedDiscount.asMutable({
    deep: true
  });
});
export var StartRecordSelector = createSelector([PageNumberSelector, SessionSelectors.PageSizePreferenceSelector, RecordCountSelector], function (pageNumber, pageSize, recordCount) {
  if (recordCount <= 0) {
    return 0;
  }
  return (pageNumber - 1) * pageSize + 1;
});
export var SubscriptionErrorSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.subscriptionsError;
});
var DEFAULT_ERRORS = [];
export var SubscriptionFormErrorSelector = createSelector([SubscriptionErrorSelector], function (subscriptionError) {
  return subscriptionError && subscriptionError.message ? [subscriptionError.message] : DEFAULT_ERRORS;
});
export var SubscriptionsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.data;
});
var createBriefSubscriptionViewModels = function createBriefSubscriptionViewModels(subscription) {
  var firstSubscriptionItem = subscription.Items ? subscription.Items[0] : {};
  return {
    id: subscription.Id,
    isAligned: subscription.Items && subscription.Items.length > 1 ? true : false,
    items: subscription.Items,
    name: subscription.Items && subscription.Items.length > 1 ? "".concat(subscription.PaymentInstrumentTypeName, " - ").concat(subscription.PaymentInstrumentName) : pathOr('', ['Product', 'Name'], firstSubscriptionItem),
    numberItems: subscription.Items ? subscription.Items.length : 0,
    paymentInstrument: {
      name: subscription.PaymentInstrumentName,
      typeName: subscription.PaymentInstrumentTypeName
    },
    pricingPlan: pathOr({}, ['PricingPlan'], firstSubscriptionItem),
    renewal: {
      amount: subscription.RenewAmount,
      billingCycleName: pathOr('', ['ChangePricingPlan', 'SubscriptionBillingCycleName'], firstSubscriptionItem) || pathOr('', ['PricingPlan', 'SubscriptionBillingCycleName'], firstSubscriptionItem),
      reminderDate: subscription.ReminderDate,
      date: subscription.CycleDate,
      type: i18n.translate(subscription.Type === SubscriptionConstants.SUBSCRIPTION_TYPE.MANUAL ? LocaleKeys.SUBSCRIPTIONS.MANUAL_RENEWAL : LocaleKeys.SUBSCRIPTIONS.AUTOMATIC_RENEWAL)
    },
    status: subscription.Status
  };
};
var DEFAULT_BRIEF_SUBSCRIPTIONS = [];
export var BriefSubscriptionsSelector = createSelector([SubscriptionsSelector], function (subscriptions) {
  if (subscriptions) {
    return subscriptions.map(createBriefSubscriptionViewModels);
  }
  return DEFAULT_BRIEF_SUBSCRIPTIONS;
});
export var SubscriberHasAnyAlignedSubscriptionsSelector = createSelector([SubscriptionsSelector], function (subscriptions) {
  return any(function (sub) {
    return sub.Items && sub.Items.length > 1;
  }, subscriptions || []);
});
var getPaymentInstrumentName = function getPaymentInstrumentName(subscription) {
  return subscription.PaymentInstrumentName;
};
export var SubscriptionsPaymentInstruments = createSelector([SubscriptionsSelector], function (subscriptions) {
  return subscriptions ? pipe(map(getPaymentInstrumentName), uniq)(subscriptions) : [];
});
var DEFAULT_TABLE_DATA = [];
export var TableDataSelector = createSelector([SubscriptionsSelector], function (subscriptions) {
  if (subscriptions) {
    var translatedYes = i18n.translate(LocaleKeys.YES);
    var translatedNo = i18n.translate(LocaleKeys.NO);
    return subscriptions.map(function (subscription) {
      var renewEval = subscription.Renew;
      return subscription.set('Renew', renewEval ? translatedYes : translatedNo).set('productNames', subscription.Items.map(function (item) {
        return "".concat(item.Product.Name, " - ").concat(item.PricingPlan.Name);
      }).join(', '));
    });
  }
  return DEFAULT_TABLE_DATA;
});
export var TotalPagesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.pageCount;
});
export var RemoveSubscriptionNameSelector = createSelector([RemoveChildSubscriptionSelector, CurrentSubscriptionSelector], function (childSubscription, subscription) {
  if (childSubscription) {
    return "".concat(childSubscription.Product.Name, " - ").concat(childSubscription.PricingPlan.Name);
  }
  return subscription && subscription.Items ? subscription.Items.map(function (item) {
    return "".concat(item.Product.Name, " - ").concat(item.PricingPlan.Name);
  }).join(', ') : '';
});
export var CurrentSubscriptionHasTerminationFee = createSelector([CurrentSubscriptionSelector], function (subscription) {
  return subscription && subscription.TerminationFee ? Number(subscription.TerminationFee) > 0 : false;
});
export var CurrentSubscriptionHasLateFeeSelector = createSelector([CurrentSubscriptionSelector], function (subscription) {
  return subscription && subscription.LateFee ? Number(subscription.LateFee) > 0 : false;
});
var SubscriptionOrderStatusSelector = createSelector([CurrentSubscriptionSelector], function (subscription) {
  return subscription && subscription.OrderStatus;
});
var DEFAULT_FIRST_ITEM = {};
var CurrentSubscriptionFirstItem = createSelector([CurrentSubscriptionSelector], function (subscription) {
  return subscription && subscription.Items ? subscription.Items[0] : DEFAULT_FIRST_ITEM;
});
export var EditSubscriptionItemIdSelector = createSelector([SelectedCustomerSelector, ChildSubscriptionIdSelector], function (selectedCustomer, childSubscriptionIdFromRoute) {
  return childSubscriptionIdFromRoute ? childSubscriptionIdFromRoute : selectedCustomer.subscriptions.editSubscriptionItemId;
});
export var EditSubscriptionItemSelector = createSelector([EditSubscriptionItemIdSelector, CurrentSubscriptionSelector],
// This returns the original of the current subsc that is being edited
//  to get this subsc with its current (edited) changes use ModifiedSubscriptionItemSelector
function (editItemId, subscription) {
  if (isNil(subscription) || isNil(subscription.Items)) {
    return DEFAULT_FIRST_ITEM;
  }
  return editItemId ? find(function (item) {
    return item.Id === editItemId;
  }, subscription.Items) || DEFAULT_FIRST_ITEM : subscription.Items[0];
});
export var InstancePropertySelector = createSelector([EditSubscriptionItemSelector], function (subscription) {
  return subscription.InstanceProperties;
});
export var OrderNumberSelector = createSelector([CurrentSubscriptionFirstItem], function (firstItem) {
  return firstItem.OrderNumber;
});
export var OrderIdSelector = createSelector([CurrentSubscriptionFirstItem], function (firstItem) {
  return firstItem.OrderId;
});
export var CurrentSubscriptionPricingPlanSelector = createSelector([CurrentSubscriptionFirstItem], function (firstItem) {
  return pathOr({}, ['PricingPlan'], firstItem);
});
export var CurrentSubscriptionProductSelector = createSelector([CurrentSubscriptionFirstItem], function (firstItem) {
  if (firstItem && firstItem.Product) {
    return firstItem.Product;
  }
  return {};
});
export var CurrencySelector = createSelector([CurrentSubscriptionFirstItem], function (firstItem) {
  return pathOr('', ['ChangePricingPlan', 'Currency'], firstItem) || pathOr('', ['PricingPlan', 'Currency'], firstItem);
});
export var BrandableCurrencyNameSelector = createSelector([CurrentSubscriptionFirstItem], function (firstItem) {
  return pathOr('', ['ChangePricingPlan', 'BrandableCurrencyName'], firstItem) || pathOr('', ['PricingPlan', 'BrandableCurrencyName'], firstItem);
});
export var BillingCycleSelector = createSelector([CurrentSubscriptionFirstItem], function (firstItem) {
  return pathOr('', ['ChangePricingPlan', 'SubscriptionBillingCycleName'], firstItem) || pathOr('', ['PricingPlan', 'SubscriptionBillingCycleName'], firstItem);
});
var SubscriptionShippingInformation = createSelector([CurrentSubscriptionSelector], function (currentSubscription) {
  if (!currentSubscription || !currentSubscription.Items || !currentSubscription.Items.length) {
    return null;
  }

  // use shipping information from first item that has it
  // currently we only support all aligned subscription items having the same address
  return pathOr(null, ['ShippingInformation'], currentSubscription.Items.find(function (item) {
    return item.ShippingInformation && !isEmpty(item.ShippingInformation);
  }));
});
export var ShippingAddressSelector = createSelector([SubscriptionShippingInformation], function (shippingInfo) {
  return pathOr(null, ['Address'], shippingInfo);
});
export var ShippingMethodNameSelector = createSelector([SubscriptionShippingInformation, MetadataSelectors.shippingMethods.ShippingMethodsSelector], function (shippingInfo, shippingMethodsMetadata) {
  var shippingMethodId = pathOr(null, ['ShippingMethodId'], shippingInfo);
  if (!shippingMethodsMetadata || !shippingMethodsMetadata.length || !shippingMethodId) {
    return null;
  }
  var shippingMethodConfig = shippingMethodsMetadata.find(function (method) {
    return method.Id === shippingMethodId;
  });
  return shippingMethodConfig ? shippingMethodConfig.Name : null;
});
export var ActiveAddressesWithCurrentSubscriptionAddressSelector = createSelector([CurrentActiveAddressesSelector, ShippingAddressSelector], function (subscriberActiveAddresses, subscriptionShippingAddress) {
  var activeAddressMatchesIdAndName = subscriberActiveAddresses.find(function (addr) {
    return subscriptionShippingAddress && addr.Id === subscriptionShippingAddress.Id && addr.ShipToName === subscriptionShippingAddress.ShipToName;
  });
  var retVal = subscriptionShippingAddress && !activeAddressMatchesIdAndName ? [subscriptionShippingAddress] : [];
  return retVal.concat(subscriberActiveAddresses);
});

// Created to replace ShippingAddressSelector as a workaround
// Because the API is not updating the ShipToName field PD-485248
// Should be used again once the endpoint SubscriberManagement/UpdateSubscription gets fixed
export var ShippingAddressSelectorForSubscriptions = createSelector(CurrentActiveAddressesSelector, SubscriptionShippingInformation, ShippingAddressSelector, function (addresses, shippingInfo, shippingAddress) {
  var shippingAddressWithInfo = (addresses || EMPTY_ARRAY).find(function (addr) {
    return shippingAddress && shippingAddress.Id === addr.Id;
  });
  var address = pathOr(null, ['Address'], shippingInfo);
  return address && shippingAddressWithInfo ? address.set('ShipToName', pathOr(null, ['ShipToName'], shippingAddressWithInfo)) : address;
});
export var RenewalUnderEditSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.renewalUnderEdit.asMutable();
});
export var SubscriptionProductIdSelector = createSelector([EditSubscriptionItemSelector], function (subscription) {
  return path(['Product', 'Id'], subscription);
});
export var SubscriptionPricingPlanIdSelector = createSelector([EditSubscriptionItemSelector], function (subscription) {
  return path(['PricingPlan', 'Id'], subscription);
});
export var SubscriptionCouponCodeSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.couponCode;
});
export var UserHasOrderingPermissionSelector = createSelector([SessionSelectors.UserSecurityAttributesSelector], function (userSecurityAttributes) {
  return PermissionService.hasAdminAccess(userSecurityAttributes, ORDERING_ACCESS);
});
export var UserCanRemoveDiscountSelector = createSelector([SessionSelectors.UserSecurityAttributesSelector], function (userSecurityAttributes) {
  return PermissionService.hasAccess(userSecurityAttributes, SUBSCRIBER_PRODUCT_REMOVE_DISCOUNT_ACCESS);
});
export var UserCanIssueCreditToAlternatePaymentInstrument = createSelector([SessionSelectors.UserSecurityAttributesSelector], function (userSecurityAttributes) {
  return PermissionService.hasAccess(userSecurityAttributes, ISSUE_CREDIT_TO_ALTERNATE_PAYMENT_INSTRUMENT);
});
export var UserCanRemoveSubscriptionSelector = createSelector([CurrentSubscriptionSelector, IsSubscriptionActiveSelector, IsSubscriptionSuspendedSelector, SessionSelectors.UserSecurityAttributesSelector, SubscriptionOrderStatusSelector], function (currentSubscription, isActive, isSuspended, userSecurityAttributes, orderStatus) {
  var flag = path(['Policy', 'Removable'], currentSubscription) === true && (isActive || isSuspended) && ORDER_STATUSES.OPEN !== orderStatus && PermissionService.hasAdminAccess(userSecurityAttributes, SUBSCRIPTION_ACCESS);
  if (pathOr([], ['Items'], currentSubscription).length > 1) {
    // Aligned Subscription
    return flag;
  } else {
    return flag && path(['Policy', 'Removable'], currentSubscription) === true;
  }
});
export var UserCanReplaceSubscriptionSelector = createSelector([SubscriptionOrderStatusSelector, IsSubscriptionActiveSelector, UserHasOrderingPermissionSelector], function (orderStatus, isActive, userHasOrderingPermission) {
  return orderStatus === ORDER_STATUSES.COMPLETE && isActive && userHasOrderingPermission;
});
var UserCanRenewOrModifySelector = createSelector([IsSubscriptionActiveSelector, UserHasOrderingPermissionSelector, SubscriptionOrderStatusSelector, CurrentSubscriptionSelector], function (isActive, userHasOrderingPermission, orderStatus, currentSubscription) {
  return isActive && userHasOrderingPermission && orderStatus !== ORDER_STATUSES.OPEN && !currentSubscription.offeringThumbnail;
});
export var UserCanModifySelector = UserCanRenewOrModifySelector;
export var UserCanRenewSelector = UserCanRenewOrModifySelector;
export var CurrentSubscriptionHasExtensionsSelector = createSelector([CurrentSubscription], function (currentSubscription) {
  if (doesCurrentSubscriptionHaveItems(currentSubscription) && currentSubscription.Items[0].SubscriptionExtensions) {
    return currentSubscription.Items[0].SubscriptionExtensions.length ? true : false;
  }
  return false;
});
export var UserCanRemoveExtensionSelector = createSelector([CurrentSubscriptionHasExtensionsSelector, SessionSelectors.UserSecurityAttributesSelector], function (hasExtensions, userSecurityAttributes) {
  return hasExtensions && PermissionService.hasAdminAccess(userSecurityAttributes, SUBSCRIPTION_ACCESS);
});
export var UserCanUpdateSubscriptionSelector = createSelector([IsSubscriptionActiveSelector, UserHasOrderingPermissionSelector], function (isActive, userHasOrderingPermissionSelector) {
  return isActive && userHasOrderingPermissionSelector;
});
export var UserCanRestoreSubscriptionSelector = createSelector([CurrentSubscriptionSelector, IsSubscriptionSuspendedSelector, SessionSelectors.UserSecurityAttributesSelector], function (currentSubscription, isSuspended, userSecurityAttributes) {
  return path(['Policy', 'Renewable'], currentSubscription) === true && isSuspended && PermissionService.hasAdminAccess(userSecurityAttributes, ORDERING_ACCESS);
});
export var subscriptionActionListSelector = createSelector([UserCanRestoreSubscriptionSelector, IsSubscriptionSuspendedSelector], function (canRestoreSubscription, isSuspended) {
  var actionList = [{
    id: 'VIEW',
    category: i18n.translate(LocaleKeys.VIEW)
  }];
  if (canRestoreSubscription && isSuspended) {
    actionList.push({
      id: 'RESTORE',
      category: i18n.translate(LocaleKeys.RESTORE)
    });
  } else {
    actionList.push({
      id: 'MODIFY',
      category: i18n.translate(LocaleKeys.MODIFY)
    });
  }
  actionList.push({
    id: 'REMOVE',
    category: i18n.translate(LocaleKeys.REMOVE)
  });
  return actionList;
});
export var UserCanTransitionOfferSelector = createSelector([CurrentSubscriptionOfferingDetailsSelector, MetadataSelectors.codes.MetadataCodeTypeIntegerSelector(MetadataConstants.codes.TransitionConfiguration)], function (currentSubscriptionOfferingDetails, transitionConfiguration) {
  var allowTransition = BoolHelper.getBoolOrDefault(pathOr(null, [0, 'AdditionalProperties', 0, 'Value'], transitionConfiguration), false);
  return allowTransition && pathOr(false, ['Subscription'], currentSubscriptionOfferingDetails) && !hasBulkOfferOptions(currentSubscriptionOfferingDetails) && !pathOr(false, ['OrderContractInstance'], currentSubscriptionOfferingDetails) && !hasPendingChange(currentSubscriptionOfferingDetails);
});
export var SubscriptionDetailMoreOptionsSelector = createSelector([CurrentSubscriptionSelector, UserCanRemoveSubscriptionSelector, UserCanReplaceSubscriptionSelector, UserCanUpdateSubscriptionSelector, UserHasOrderingPermissionSelector, UserCanModifySelector, UserCanRemoveExtensionSelector, UserCanTransitionOfferSelector], function (currentSubscription, userCanRemove, userCanReplace, userCanUpdate, userHasOrderingPermission, userCanModify, userCanRemoveExtension, userCanTransitionOffer) {
  var moreOptions = [];
  if (currentSubscription && !currentSubscription.offeringThumbnail) {
    if (userCanUpdate) {
      moreOptions.push({
        id: SubscriptionConstants.APPLY_DISCOUNT_OPTION_ID,
        title: i18n.translate(LocaleKeys.SUBSCRIPTIONS.APPLY_DISCOUNT)
      });
    }
    if (userCanModify && !currentSubscription.isAligned) {
      moreOptions.push({
        id: SubscriptionConstants.MODIFY_SUBSCRIPTION,
        title: i18n.translate(LocaleKeys.SUBSCRIPTIONS.MODIFY)
      });
    }
    if (userCanRemoveExtension) {
      moreOptions.push({
        id: SubscriptionConstants.REMOVE_SUBSCRIPTION_EXTENSION_OPTION_ID,
        title: i18n.translate(LocaleKeys.SUBSCRIPTIONS.REMOVE_EXTENSION.REMOVE_EXTENSION)
      });
    }
    if (userCanRemove) {
      moreOptions.push({
        id: SubscriptionConstants.REMOVE_SUBSCRIPTION_OPTION_ID,
        title: currentSubscription.isAligned ? i18n.translate(LocaleKeys.SUBSCRIPTIONS.REMOVE_SUBSCRIPTION.REMOVE_ALL) : i18n.translate(LocaleKeys.SUBSCRIPTIONS.REMOVE_SUBSCRIPTION.REMOVE)
      });
    }
    if (userCanReplace && !currentSubscription.isAligned) {
      moreOptions.push({
        id: SubscriptionConstants.REPLACE_SUBSCRIPTION_OPTION_ID,
        title: i18n.translate(LocaleKeys.SUBSCRIPTIONS.REPLACE_SUBSCRIPTION)
      });
    }
  } else if (currentSubscription && currentSubscription.offeringThumbnail && currentSubscription.OrderStatus === ORDER_STATUSES.COMPLETE) {
    if (userCanRemove) {
      moreOptions.push({
        id: SubscriptionConstants.REMOVE_SUBSCRIPTION_OFFER_OPTION_ID,
        title: i18n.translate(LocaleKeys.REMOVE_OFFER.REMOVE_OFFER)
      });
    }
    if (userCanUpdate) {
      moreOptions.push({
        id: SubscriptionConstants.MODIFY_SUBSCRIPTION_OFFER_OPTION_ID,
        title: i18n.translate(LocaleKeys.EDIT_OFFER.EDIT_OFFER_ORDER)
      });
    }
    if (userCanTransitionOffer) {
      moreOptions.push({
        id: SubscriptionConstants.CHANGE_SUBSCRIPTION_OFFER_OPTION_ID,
        title: i18n.translate(LocaleKeys.CUSTOMER_DASHBOARD.CHANGE_OFFER)
      });
    }
  }
  if (userHasOrderingPermission) {
    moreOptions.push({
      id: SubscriptionConstants.VIEW_PAYMENT_HISTORY_OPTION_ID,
      title: i18n.translate(LocaleKeys.SUBSCRIPTIONS.VIEW_PAYMENT_HISTORY)
    });
  }
  return moreOptions;
});
export var PricingPlanOptionsSelector = createSelector([EditSubscriptionItemSelector, SubscriptionPricingPlanIdSelector], function (subscription, pricingPlanId) {
  if (subscription && pricingPlanId) {
    return pipe(pathOr([], ['ChangeOptions']), pluck('PricingPlan'), pluck('Id'), append(pricingPlanId))(subscription);
  }
  return [];
});
export var ModifyRenewalSubscriptionItemSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.subscriptions.modifyRenewalSubscriptionItem;
});

/*
    Note about Subscription Extensions:
    We are assuming the backend is returning the extensions in the order in which they were respectively purchased.
    Also only active extensions or extensions that will be active in the future get returned in the payload.
    Cancelled subscription extensions should not show up.
*/

export var CurrentSubscriptionExtensionsSelector = createSelector([CurrentSubscription, CurrentSubscriptionHasExtensionsSelector], function (currentSubscription, hasExtensions) {
  return hasExtensions ? currentSubscription.Items[0].SubscriptionExtensions : [];
});

/* This is the initial pricing plan for the subscription */
export var OriginalPricingPlanSelector = createSelector([CurrentSubscription], function (currentSubscription) {
  if (doesCurrentSubscriptionHaveItems(currentSubscription)) {
    return currentSubscription.Items[0].PricingPlan ? currentSubscription.Items[0].PricingPlan : {};
  }
  return {};
});
export var IsASubscriptionExtensionActive = createSelector([CurrentSubscriptionHasExtensionsSelector, CurrentSubscriptionExtensionsSelector], function (hasExtensions, extensions) {
  if (!(hasExtensions && Array.isArray(extensions) && extensions.length > 0)) {
    return false;
  }
  return extensions.some(function (currentExtension) {
    return currentExtension.CurrentlyActive;
  });
});
export var ActiveExtensionSelector = createSelector([CurrentSubscriptionHasExtensionsSelector, CurrentSubscriptionExtensionsSelector, IsASubscriptionExtensionActive], function (hasExtensions, extensions, isExtensionActive) {
  if (hasExtensions && isExtensionActive) {
    return extensions.find(function (currentExtension) {
      return currentExtension.CurrentlyActive;
    });
  } else {
    return {};
  }
});

/*
    The PricingPlanDictionarySelector is only being used to determine the PP name for an
    extension, since the name is not on Subscription Extensions returned from the API.
*/
export var PricingPlanDictionarySelector = createSelector([CurrentSubscription, CurrentSubscriptionHasExtensionsSelector], function (currentSubscription, hasExtensions) {
  if (hasExtensions && currentSubscription) {
    return constructPricingPlanDictionary(currentSubscription);
  }
  return {};
});
export var CurrentPricingPlanSelector = createSelector([OriginalPricingPlanSelector, IsASubscriptionExtensionActive, ActiveExtensionSelector, PricingPlanDictionarySelector], function (originalPP, isAnExtensionActive, activeExtension, pricingPlanDictionary) {
  if (!isAnExtensionActive) {
    return originalPP;
  } else {
    var currentPP = Object.assign({}, activeExtension);
    currentPP.Name = pricingPlanDictionary[activeExtension.PricingPlanId];
    return currentPP;
  }
});
export var BackButtonRouteForSubscriptionDetailsSelector = createSelector([SessionSelectors.RouteHistorySelector], function (history) {
  var currentEntry, foundEntry;
  if (history) {
    for (var i = history.length - 1; i >= 0; i--) {
      currentEntry = history[i];
      if (!foundEntry && (currentEntry.name === DASHBOARD_ROUTE || currentEntry.name === SUBSCRIPTION_LIST_ROUTE)) {
        foundEntry = currentEntry;
      }
    }
  }
  return foundEntry ? foundEntry.name : DASHBOARD_ROUTE;
});